import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import isArray from 'lodash/isArray';
import isBoolean from 'lodash/isBoolean';
import isNumber from 'lodash/isNumber';

/**
 * This custom component is inspired by Primereact/Carousel. This is a simple version
 * of Primereact Carousel component. The original souce code can be found here:
 * https://github.com/primefaces/primereact/blob/master/src/components/carousel/Carousel.js
 */
class WrappedCarousel extends Component {
	constructor(props) {
		super(props);

		// see `defaultProps` for default values
		const { allowAutoplay, autoplayInterval, circular, page, items } = this.props;

		this.state = {
			allowAutoplay,
			autoplayInterval,
			circular,
			items,
			showAnimation: false,
			page: isNumber(page) ? page : 0,
		};

		this.totalIndicators = isArray(items) ? items.length : 0;
		this.interval = null;
	}

	componentDidMount() {
		const { allowAutoplay } = this.state;

		if (allowAutoplay) {
			this.startAutoplay();
		}
	}

	componentDidUpdate(prevProps, prevState) {
		const { allowAutoplay, circular, items, page } = this.props;
		const { allowAutoplay: currentAllowAutoplay, page: currentPage } = this.state;
		const newState = {
			page: currentPage,
			autoplay: currentAllowAutoplay,
		};
		let shouldUpdate = false;

		// update if `page` is changed
		if (isNumber(page) && prevProps.page !== page) {
			newState.page = page;
			shouldUpdate = true;
		}

		// update if `items` is changed
		if (isArray(items) && isArray(prevProps.items) && prevProps.items.length !== items.length) {
			newState.items = items;
			this.totalIndicators = items.length;
			shouldUpdate = true;
		}

		// update if `allowAutoplay` is changed
		if (isBoolean(allowAutoplay) && allowAutoplay !== prevState.allowAutoplay) {
			newState.allowAutoplay = allowAutoplay;
			shouldUpdate = true;
		}

		// update if `circular` is changed
		if (isBoolean(circular) && circular !== prevState.circular) {
			newState.circular = circular;
			shouldUpdate = true;
		}

		if (shouldUpdate) {
			if (this.totalIndicators === 0) {
				newState.page = 0;
			} else if (newState.page > this.totalIndicators - 1) {
				newState.page = this.totalIndicators - 1;
			}

			this.updateState(newState, () => {
				if (newState.allowAutoplay) {
					this.startAutoplay();
				} else {
					this.stopAutoplay();
				}
			});
		}
	}

	componentWillUnmount() {
		this.stopAutoplay();
	}

	// this method is created to bypass eslint warning
	// warning: Do not use setState in componentDidUpdate
	updateState = (newState, callback) => {
		this.setState(newState, callback);
	};

	// stop auto advancing
	stopAutoplay = () => {
		if (this.interval) {
			clearInterval(this.interval);

			this.interval = null;
		}
	};

	// resume auto advancing
	startAutoplay = () => {
		const { autoplayInterval } = this.state;
		const showAnimation = true;

		this.stopAutoplay();

		this.interval = setInterval(() => {
			this.step(-1, showAnimation);
		}, autoplayInterval);
	};

	/**
	 * Navigate to the next or previous slide.
	 * @param {number} dir `-1` to advance to the next item and `1` to go back.
	 * @param {boolean} showAnimation `true` to show fade-in-fade-out animation.
	 */
	step = (dir, showAnimation) => {
		let { page, circular } = this.state;

		// next page
		page -= dir;

		// when there are no more items to advance to (next)
		if (page === this.totalIndicators && dir === -1) {
			page = circular ? 0 : this.totalIndicators - 1;
		}
		// when there are no more items to go back to (prev)
		else if (page === -1 && dir === 1) {
			page = circular ? this.totalIndicators - 1 : 0;
		}

		this.setState({
			page,
			showAnimation,
		});
	};

	// advance to the next item
	navForward = e => {
		const showAnimation = false;

		this.step(-1, showAnimation);

		if (e.cancelable) {
			e.preventDefault();
		}
	};

	// go back to the previous item
	navBackward = e => {
		const showAnimation = false;

		this.step(1, showAnimation);

		if (e.cancelable) {
			e.preventDefault();
		}
	};

	// stop autoplay when user hovers over carousel
	onMouseEnter = () => {
		this.stopAutoplay();
	};

	// resume autoplay when user hovers out carousel
	onMouseLeave = () => {
		const { allowAutoplay } = this.state;

		if (allowAutoplay) {
			this.startAutoplay();
		}
	};

	// DOM manipulation - add 'p-carousel-item-hidden' className to hide the element.
	onTransitionEnd = el => {
		const { target } = el;

		// hide the element by assigning 'p-carousel-item-hidden' className
		if (target) {
			if (target.classList) {
				target.classList.add('p-carousel-item-hidden');
			} else {
				target.className += ` p-carousel-item-hidden`;
			}
		}
	};

	renderContent() {
		const { containerClassName } = this.props;
		const items = this.renderItems();
		const containerCls = classNames('p-carousel-container', containerClassName);

		return (
			<div className={containerCls}>
				<div className="p-carousel-items-content">
					<div className="p-carousel-items-container">{items}</div>
				</div>
				{this.renderBackwardNavigator()}
				{this.renderForwardNavigator()}
			</div>
		);
	}

	renderBackwardNavigator() {
		const { circular, page } = this.state;
		const isDisabled = (!circular && page === 0) || this.totalIndicators === 0;
		const buttonClassName = classNames('p-carousel-prev p-link', {
			'p-disabled': isDisabled,
		});

		return (
			<button
				type="button"
				className={buttonClassName}
				onClick={this.navBackward}
				disabled={isDisabled}
				data-qa="carousel-prev-button"
				{...{ dataqa: 'carousel-prev-button' }}
			>
				<span className="p-carousel-prev-icon pi pi-chevron-left" />
			</button>
		);
	}

	renderForwardNavigator() {
		const { circular, page } = this.state;
		const isDisabled =
			(!circular && page === this.totalIndicators - 1) || this.totalIndicators === 0;
		const buttonClassName = classNames('p-carousel-next p-link', {
			'p-disabled': isDisabled,
		});

		return (
			<button
				type="button"
				className={buttonClassName}
				onClick={this.navForward}
				disabled={isDisabled}
				data-qa="carousel-next-button"
				{...{ dataqa: 'carousel-next-button' }}
			>
				<span className="p-carousel-next-icon pi pi-chevron-right" />
			</button>
		);
	}

	renderItems() {
		if (this.totalIndicators > 0) {
			const { itemTemplate } = this.props;
			const { items, page, showAnimation } = this.state;
			const prevPage = page - 1 < 0 ? this.totalIndicators - 1 : page - 1;

			const activeItem = {
				page,
				template: items[page],
				cls: 'p-carousel-item-active',
			};

			const prevActiveItem =
				showAnimation === true
					? {
							page: prevPage,
							template: items[prevPage],
							cls: 'p-carousel-item-active-prev',
					  }
					: null;

			/**
			 * Dev note:
			 * 1. 'p-carousel-item-active-prev' item should always be placed AFTER
			 *    'p-carousel-item-active' item in the DOM tree because the fade-
			 *    out animation effect is visible.
			 * 2. when transition ends, item is assigned `p-carousel-item-hidden`
			 *    class to be hidden so user could click on the active item.
			 * 3. when `showAnimation=false`, 'p-carousel-item-active-prev' item
			 *    doesn't need to be rendered.
			 */

			return [activeItem, prevActiveItem]
				.filter(item => item !== null)
				.map(item => (
					<div
						key={item.page}
						className={`p-carousel-item ${item.cls}`}
						onMouseEnter={this.onMouseEnter}
						onMouseLeave={this.onMouseLeave}
						onTransitionEnd={this.onTransitionEnd}
					>
						{itemTemplate(item.template)}
					</div>
				));
		}

		return null;
	}

	render() {
		const { className, contentClassName, width, height } = this.props;
		const carouselCls = classNames('p-carousel p-component p-carousel-horizontal', className);
		const carouselContentCls = classNames('p-carousel-content', contentClassName);

		return (
			<div className="common-ui-media-carousel" style={{ width, height }}>
				<div className={carouselCls}>
					<div className={carouselContentCls}>{this.renderContent()}</div>
				</div>
			</div>
		);
	}
}

WrappedCarousel.defaultProps = {
	items: [],
	page: 0,
	className: null,
	itemTemplate: null,
	circular: true,
	allowAutoplay: true,
	autoplayInterval: 5000,
	contentClassName: null,
	containerClassName: null,
	width: '100%',
};

WrappedCarousel.propTypes = {
	items: PropTypes.array,
	page: PropTypes.number,
	className: PropTypes.string,
	itemTemplate: PropTypes.any,
	circular: PropTypes.bool,
	allowAutoplay: PropTypes.bool,
	autoplayInterval: PropTypes.number,
	contentClassName: PropTypes.string,
	containerClassName: PropTypes.string,
	width: PropTypes.any,

	// Required.
	// This will be used to calculate the navigation buttons' position
	height: PropTypes.any.isRequired,
};

export default WrappedCarousel;
